import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "@/lib/data";
import { track } from "@/lib/analytics";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 w-full z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-[#050505]/85 backdrop-blur-xl backdrop-saturate-150 border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2 group" data-testid="logo-link">
          <span className="font-heading text-xl tracking-[0.25em] uppercase text-white group-hover:text-gold transition-colors duration-300">TAPSS</span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-[13px] tracking-[0.15em] uppercase text-white/55 hover:text-gold transition-colors duration-300"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <a
          href="#book-call"
          onClick={() => track("booking_click", "header")}
          data-testid="header-book-cta"
          className="hidden md:inline-flex items-center px-6 py-2.5 rounded-full border border-white/20 text-[12px] tracking-[0.18em] uppercase text-white hover:bg-gold hover:text-black hover:border-gold transition-colors duration-300"
        >
          Book a Call
        </a>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#050505]/95 backdrop-blur-xl border-b border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="text-sm tracking-[0.15em] uppercase text-white/70">
                  {n.label}
                </a>
              ))}
              <a href="#book-call" onClick={() => setOpen(false)} className="mt-2 inline-flex justify-center px-6 py-3 rounded-full bg-gold text-black text-xs tracking-[0.18em] uppercase">
                Book a Call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
